import { useContext, useEffect } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';

import { inventoryStore } from 'app/components/pages/Extranet/ExtranetInventory/Modern/InventoryStore';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import type { ExtranetHotelRoom } from 'app/typings/core/extranet/hotel/rooms';
import { assertNever } from 'app/utils/typing';

import { ModernExtraAddOn } from '../../../ExtraAddOn/Modern/ModernExtraAddOn';
import { ModernExtraBedModal } from '../../../ExtraBed/Modal/Modern/ModernExtraBedModal';
import type { BedTaskInformation, Task } from '../Types';
import { TaskName } from '../Types';

type RenderModalProps = {
  task: Task;
  onCompleteTask: () => void;
  rooms?: ExtranetHotelRoom[];
};

export const RenderModal = ({
  task,
  onCompleteTask,
  rooms,
}: RenderModalProps) => {
  const { openTask, clearOpenTask } = useContext(TaskCenterContext);
  const { setPricingDrawerOpen, setOpenPricingFromTask } = useStore(
    inventoryStore,
    useShallow((state) => ({
      setPricingDrawerOpen: state.setPricingDrawerOpen,
      setOpenPricingFromTask: state.setOpenPricingFromTask,
    }))
  );

  useEffect(() => {
    if (
      task.taskName !== TaskName.LastMinutePricingRule &&
      task.taskName !== TaskName.PricingRule
    ) {
      return;
    }

    if (task.taskName !== openTask) {
      return;
    }

    setOpenPricingFromTask(true);
    setPricingDrawerOpen(true);
    clearOpenTask();
  }, [
    task.taskName,
    openTask,
    setOpenPricingFromTask,
    setPricingDrawerOpen,
    clearOpenTask,
  ]);

  if (task.taskName !== openTask) {
    return <></>;
  }

  switch (task.taskName) {
    case TaskName.LastMinutePricingRule:
      return <></>;

    case TaskName.BabyCot:
    case TaskName.GuestBed:
      return rooms ? (
        <ModernExtraBedModal
          bedTypeId={(task as Extract<Task, BedTaskInformation>).categoryId}
          rooms={rooms}
          isOpen={task.taskName === openTask}
          onCompleteTask={onCompleteTask}
        />
      ) : (
        <></>
      );

    case TaskName.EarlyCheckin:
    case TaskName.LateCheckout:
      return (
        <ModernExtraAddOn
          isOpen={task.taskName === openTask}
          task={task}
          onCompleteTask={onCompleteTask}
        />
      );

    case TaskName.PricingRule:
    case TaskName.AttractExtraGuests:
    case TaskName.ConfigureChildPrice:
    case TaskName.AttractSingleGuests:
    case TaskName.AutoInventory:
    case TaskName.VirtualCards:
      return <></>;
    default:
      assertNever(task.taskName);
  }
};
